/**
 * RBAC vocabulary — permission keys, authorization scopes, and the role matrix.
 *
 * This module is the single definition of who may do what. The API uses it to
 * decide what is *allowed*; the frontend uses it to decide what to *render*.
 * Because both compile against the same table, a permission cannot be enforced
 * in one place and forgotten in the other.
 *
 * The frontend copy is a convenience only. Authorization is always re-evaluated
 * server-side against the loaded entity — RBAC-006, RBAC-008.
 *
 * Traces: RBAC-001…010, SEC-005, SEC-011, SEC-012.
 */

import { z } from 'zod';
import type { RoleKey } from './enums';

// ---------------------------------------------------------------------------
// Scopes
// ---------------------------------------------------------------------------

/**
 * A permission answers "may this action be performed?".
 * A scope answers "over which rows?". Both must pass.
 *
 * Scope compiles to a SQL predicate applied to every query. There is no
 * unscoped read path in the system — ADR-011.
 */
export const SCOPES = ['ANY', 'OFFICE', 'PARTICIPANT', 'OWN'] as const;
export const scopeSchema = z.enum(SCOPES);
export type Scope = z.infer<typeof scopeSchema>;

/**
 * Breadth ordering, widest first. Used to answer "does the caller's scope
 * subsume the scope this operation needs?" — never used to widen a scope.
 */
const SCOPE_BREADTH: Record<Scope, number> = {
  ANY: 4,
  OFFICE: 3,
  PARTICIPANT: 2,
  OWN: 1,
};

/** True when `held` is at least as broad as `required`. */
export function scopeSatisfies(held: Scope, required: Scope): boolean {
  return SCOPE_BREADTH[held] >= SCOPE_BREADTH[required];
}

// ---------------------------------------------------------------------------
// Permission keys
// ---------------------------------------------------------------------------

export const PERMISSIONS = [
  // offices
  'office.create',
  'office.read',
  'office.update',
  'office.disable',
  'office.archive',

  // users
  'user.create',
  'user.read',
  'user.update',
  'user.disable',
  'user.archive',
  'user.reset_password',
  'user.change_role',
  'user.change_office',
  'user.update_own_profile',

  // roles
  'role.read',
  'role.assign',

  // confirmations
  'confirmation.create',
  'confirmation.read',
  'confirmation.send',
  'confirmation.reply',
  'confirmation.resolve',
  'confirmation.close',
  'confirmation.cancel',
  'confirmation.reopen',
  'confirmation.reassign',

  // attachments
  'attachment.upload',
  'attachment.download',

  // analytics
  'dashboard.view',
  'report.view',
  'report.export',

  // audit
  'audit.read',

  // notifications
  'notification.read',
  'notification.manage_preferences',

  // sla & settings
  'sla.read',
  'sla.manage',
  'settings.read',
  'settings.update',
] as const;

export const permissionSchema = z.enum(PERMISSIONS);
export type Permission = z.infer<typeof permissionSchema>;

/** Grouping used by the admin UI and by API documentation. */
export const PERMISSION_MODULES: Record<Permission, string> = Object.fromEntries(
  PERMISSIONS.map((p) => [p, p.split('.')[0] as string]),
) as Record<Permission, string>;

// ---------------------------------------------------------------------------
// Role matrix
// ---------------------------------------------------------------------------

/**
 * Grants are `permission → widest scope this role may exercise`.
 * An absent key means the role does not hold the permission at all.
 *
 * Some grants carry an additional object-level predicate that a scope cannot
 * express — for example, only the *originating* office may resolve or close.
 * Those are listed in ORIGIN_SIDE_PERMISSIONS below and enforced by the
 * confirmation domain service, not by the scope layer.
 */
export type RoleGrants = Partial<Record<Permission, Scope>>;

const ADMIN_GRANTS: RoleGrants = {
  'office.create': 'ANY',
  'office.read': 'ANY',
  'office.update': 'ANY',
  'office.disable': 'ANY',
  'office.archive': 'ANY',

  'user.create': 'ANY',
  'user.read': 'ANY',
  'user.update': 'ANY',
  'user.disable': 'ANY',
  'user.archive': 'ANY',
  'user.reset_password': 'ANY',
  'user.change_role': 'ANY',
  'user.change_office': 'ANY',
  'user.update_own_profile': 'OWN',

  'role.read': 'ANY',
  'role.assign': 'ANY',

  'confirmation.create': 'OWN',
  'confirmation.read': 'ANY',
  'confirmation.send': 'OWN',
  'confirmation.reply': 'ANY',
  'confirmation.resolve': 'ANY',
  'confirmation.close': 'ANY',
  'confirmation.cancel': 'ANY',
  'confirmation.reopen': 'ANY',
  'confirmation.reassign': 'ANY',

  'attachment.upload': 'ANY',
  'attachment.download': 'ANY',

  'dashboard.view': 'ANY',
  'report.view': 'ANY',
  'report.export': 'ANY',

  'audit.read': 'ANY',

  'notification.read': 'OWN',
  'notification.manage_preferences': 'OWN',

  'sla.read': 'ANY',
  'sla.manage': 'ANY',
  'settings.read': 'ANY',
  'settings.update': 'ANY',
};

const OFFICE_MANAGER_GRANTS: RoleGrants = {
  'office.read': 'OFFICE',

  'user.read': 'OFFICE',
  'user.update_own_profile': 'OWN',

  'confirmation.create': 'OWN',
  'confirmation.read': 'OFFICE',
  'confirmation.send': 'OWN',
  'confirmation.reply': 'OFFICE',
  'confirmation.resolve': 'OFFICE',
  'confirmation.close': 'OFFICE',
  'confirmation.cancel': 'OFFICE',
  'confirmation.reopen': 'OFFICE',
  'confirmation.reassign': 'OFFICE',

  'attachment.upload': 'OFFICE',
  'attachment.download': 'OFFICE',

  'dashboard.view': 'OFFICE',
  'report.view': 'OFFICE',
  'report.export': 'OFFICE',

  // C-05 resolved: managers need audit visibility over their own office's
  // correspondence to perform the §10 monitoring duties. Scoped, so nothing
  // cross-office is exposed.
  'audit.read': 'OFFICE',

  'notification.read': 'OWN',
  'notification.manage_preferences': 'OWN',

  'sla.read': 'OFFICE',
};

const USER_GRANTS: RoleGrants = {
  'office.read': 'OFFICE',

  'user.read': 'OWN',
  'user.update_own_profile': 'OWN',

  'confirmation.create': 'OWN',
  'confirmation.read': 'PARTICIPANT',
  'confirmation.send': 'OWN',
  'confirmation.reply': 'PARTICIPANT',
  // ASM-002: the originating side confirms satisfaction.
  'confirmation.resolve': 'PARTICIPANT',
  // ASM-003: closing is a records-management act reserved to managers.
  'confirmation.cancel': 'OWN',

  'attachment.upload': 'PARTICIPANT',
  'attachment.download': 'PARTICIPANT',

  'dashboard.view': 'OWN',
  'report.view': 'OWN',

  'audit.read': 'OWN',

  'notification.read': 'OWN',
  'notification.manage_preferences': 'OWN',

  'sla.read': 'OFFICE',
};

/**
 * Viewer is deliberately office-scoped, not system-wide. A global read-only
 * role would be a standing cross-office exposure; if a system-wide auditor is
 * ever needed it should be a distinct, explicitly granted role.
 */
const VIEWER_GRANTS: RoleGrants = {
  'office.read': 'OFFICE',
  'user.read': 'OFFICE',
  'user.update_own_profile': 'OWN',

  'confirmation.read': 'OFFICE',
  'attachment.download': 'OFFICE',

  'dashboard.view': 'OFFICE',
  'report.view': 'OFFICE',

  'notification.read': 'OWN',
  'notification.manage_preferences': 'OWN',

  'sla.read': 'OFFICE',
};

export const ROLE_PERMISSIONS: Record<RoleKey, RoleGrants> = {
  ADMIN: ADMIN_GRANTS,
  OFFICE_MANAGER: OFFICE_MANAGER_GRANTS,
  USER: USER_GRANTS,
  VIEWER: VIEWER_GRANTS,
};

// ---------------------------------------------------------------------------
// Object-level qualifiers
// ---------------------------------------------------------------------------

/**
 * Permissions that additionally require the actor's office to be the
 * confirmation's `origin_office_id`. A scope cannot express this — it is a
 * property of the relationship between actor and record — so the confirmation
 * domain service enforces it after loading the entity.
 *
 * ASM-002 (resolve), ASM-003 (close), G-01 (cancel), G-02 (reopen).
 */
export const ORIGIN_SIDE_PERMISSIONS = [
  'confirmation.resolve',
  'confirmation.close',
  'confirmation.cancel',
  'confirmation.reopen',
] as const satisfies readonly Permission[];

/**
 * Permissions requiring the actor to be on the *recipient* side — a recipient
 * cannot be the one who reads their own outgoing confirmation into READ state.
 */
export const RECIPIENT_SIDE_PERMISSIONS = ['confirmation.reassign'] as const satisfies readonly Permission[];

export function requiresOriginSide(permission: Permission): boolean {
  return (ORIGIN_SIDE_PERMISSIONS as readonly Permission[]).includes(permission);
}

export function requiresRecipientSide(permission: Permission): boolean {
  return (RECIPIENT_SIDE_PERMISSIONS as readonly Permission[]).includes(permission);
}

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

/**
 * The scope a role holds for a permission, or `null` if it does not hold it.
 *
 * Deny-by-default: an unlisted permission is denied, so adding a permission
 * key without granting it anywhere leaves it inaccessible rather than open.
 */
export function scopeForRole(role: RoleKey, permission: Permission): Scope | null {
  return ROLE_PERMISSIONS[role][permission] ?? null;
}

export function roleHasPermission(role: RoleKey, permission: Permission): boolean {
  return scopeForRole(role, permission) !== null;
}

/** Every permission a role holds, for building the session payload. */
export function permissionsForRole(role: RoleKey): ReadonlyArray<[Permission, Scope]> {
  return Object.entries(ROLE_PERMISSIONS[role]) as Array<[Permission, Scope]>;
}

/**
 * A user's effective grants: role defaults, then per-user overrides.
 *
 * DENY always wins over GRANT regardless of order, so an override can restrict
 * but a stale GRANT can never silently re-open something explicitly denied.
 */
export interface PermissionOverride {
  readonly permission: Permission;
  readonly effect: 'GRANT' | 'DENY';
  readonly scope: Scope | null;
  readonly expiresAt: Date | null;
}

export function effectiveGrants(
  role: RoleKey,
  overrides: readonly PermissionOverride[] = [],
  now: Date = new Date(),
): RoleGrants {
  const grants: RoleGrants = { ...ROLE_PERMISSIONS[role] };
  const denied = new Set<Permission>();

  for (const override of overrides) {
    if (override.expiresAt !== null && override.expiresAt <= now) continue;

    if (override.effect === 'DENY') {
      denied.add(override.permission);
      delete grants[override.permission];
    } else if (!denied.has(override.permission) && override.scope !== null) {
      grants[override.permission] = override.scope;
    }
  }

  return grants;
}
